/**
 * 고객 관리 페이지 - 리팩토링
 *
 * Features:
 * - KPI 요약 카드 (전체/즐겨찾기/신규/휴면)
 * - 검색/정렬/즐겨찾기 필터 바
 * - 고객 리스트 카드 (HoverCard 미리보기)
 * - 페이지네이션 (사이즈 변경)
 * - 엑셀 다운로드/업로드
 * - 고객 상세 Sheet, 등록/수정 폼, 삭제 확인
 * - URL 필터 동기화
 * - CRM/EMR 확장 준비 구조
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  User,
  ChevronLeft,
  ChevronRight,
  Loader2,
  LayoutList,
  LayoutGrid,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useScrollPosition } from "@/hooks/useScrollPosition";
import { useCustomerFilters } from "@/hooks/useCustomerFilters";

import { CustomerDetailSheet, CustomerFormDialog } from "@/components/customer";
import {
  CustomerSummaryCards,
  CustomerFilterBar,
  CustomerListCard,
  CustomerGridCard,
  CustomerExcelDialog,
} from "@/components/customer/customer-list";
import type { CustomerSummaryStats } from "@/components/customer/customer-list";

import { customerApi } from "@/api/customer";
import { memoApi } from "@/api/memo";
import { queryKeys } from "@/lib/queryKeys";
import type { Customer, CustomerFormData } from "@/types/customer";

export default function CustomerListPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useScrollPosition({ key: "customer-list" });

  // 필터 훅
  const {
    filters,
    apiParams,
    setSearch,
    setStarred,
    setSortOrder,
    setPage,
    setPageSize,
    setSegment,
    setViewMode,
    sortOptions,
    pageSizeOptions,
  } = useCustomerFilters();

  // UI state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [deleteCustomer, setDeleteCustomer] = useState<Customer | null>(null);

  // 체크박스 선택 상태
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());

  // 페이지/필터 변경 시 체크 상태 초기화
  useEffect(() => {
    setCheckedIds(new Set());
  }, [
    filters.page,
    filters.searchQuery,
    filters.starred,
    filters.sortOrder,
    filters.segment,
  ]);

  // 고객 목록 조회
  const { data: customerData, isLoading } = useQuery({
    queryKey: queryKeys.customers.list(apiParams),
    queryFn: () => customerApi.getCustomers(apiParams),
  });

  // KPI 요약 통계 (필터 무관, 전체 기준 - 별도 API)
  const { data: statsData } = useQuery({
    queryKey: queryKeys.customers.summaryStats(),
    queryFn: () => customerApi.getSummaryStats(),
  });

  const summaryStats: CustomerSummaryStats = useMemo(
    () => ({
      totalElements: statsData?.total || 0,
      starredCount: statsData?.starred || 0,
      newThisMonthCount: statsData?.newThisMonth || 0,
      dormantCount: statsData?.dormant || 0,
    }),
    [statsData],
  );

  // Mutations
  const toggleStarMutation = useMutation({
    mutationFn: customerApi.toggleStar,
    onSuccess: (updatedCustomer) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      if (selectedCustomer?.id === updatedCustomer.id) {
        setSelectedCustomer(updatedCustomer);
      }
      toast({
        title: updatedCustomer.starred
          ? "즐겨찾기에 추가되었습니다."
          : "즐겨찾기가 해제되었습니다.",
      });
    },
    onError: () => {
      toast({ title: "즐겨찾기 변경에 실패했습니다.", variant: "destructive" });
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CustomerFormData) => {
      const created = await customerApi.createCustomer(data);
      // 메모가 있으면 생성 후 저장
      if (data.note?.trim()) {
        await memoApi.createMemo({
          entityType: "CUSTOMER",
          entityId: created.id,
          content: data.note.trim(),
        });
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast({ title: "고객이 등록되었습니다." });
      setFormOpen(false);
    },
    onError: () => {
      toast({ title: "고객 등록에 실패했습니다.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CustomerFormData }) =>
      customerApi.updateCustomer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast({ title: "고객 정보가 수정되었습니다." });
      setFormOpen(false);
      setEditCustomer(null);
    },
    onError: () => {
      toast({ title: "고객 수정에 실패했습니다.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: customerApi.deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      toast({ title: "고객이 삭제되었습니다." });
      setDeleteCustomer(null);
      if (selectedCustomer?.id === deleteCustomer?.id) {
        setSelectedCustomer(null);
        setDetailOpen(false);
      }
    },
    onError: () => {
      toast({ title: "고객 삭제에 실패했습니다.", variant: "destructive" });
    },
  });

  // 핸들러
  const handleSelectCustomer = useCallback((customer: Customer) => {
    setSelectedCustomer(customer);
    setDetailOpen(true);
  }, []);

  const handleToggleStar = useCallback(
    (id: number) => toggleStarMutation.mutate(id),
    [toggleStarMutation],
  );

  const handleEdit = useCallback((customer: Customer) => {
    setEditCustomer(customer);
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback((customer: Customer) => {
    setDeleteCustomer(customer);
  }, []);

  const handleFormSubmit = useCallback(
    (data: CustomerFormData) => {
      if (editCustomer) {
        updateMutation.mutate({ id: editCustomer.id, data });
      } else {
        createMutation.mutate(data);
      }
    },
    [editCustomer, createMutation, updateMutation],
  );

  const handleStarredToggle = useCallback(() => {
    setStarred(filters.starred ? undefined : true);
  }, [filters.starred, setStarred]);

  const currentPageCustomers = customerData?.content || [];

  const handleCheckCustomer = useCallback((id: number, checked: boolean) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const isAllChecked =
    currentPageCustomers.length > 0 &&
    currentPageCustomers.every((c) => checkedIds.has(c.id));

  const isIndeterminate =
    !isAllChecked && currentPageCustomers.some((c) => checkedIds.has(c.id));

  const handleCheckAll = useCallback(
    (checked: boolean) => {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        currentPageCustomers.forEach((c) => {
          if (checked) next.add(c.id);
          else next.delete(c.id);
        });
        return next;
      });
    },
    [currentPageCustomers],
  );

  const checkedCustomers = useMemo(
    () => currentPageCustomers.filter((c) => checkedIds.has(c.id)),
    [currentPageCustomers, checkedIds],
  );

  const totalPages = customerData?.totalPages || 0;
  const totalElements = customerData?.totalElements || 0;

  return (
    <div className="flex flex-col">
      {/* 헤더 */}
      <div className="sticky top-0 z-10 flex-shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4">
          <div>
            <h1 className="text-xl font-bold">고객 관리</h1>
            <p className="text-sm text-muted-foreground">
              총 {totalElements.toLocaleString()}명의 고객
            </p>
          </div>

          <div className="flex gap-2">
            <CustomerExcelDialog
              currentPageData={currentPageCustomers}
              searchTerm={filters.searchQuery}
              showStarredOnly={!!filters.starred}
              sortOrder={filters.sortOrder}
              selectedCustomers={checkedCustomers}
            />
            <Button
              onClick={() => {
                setEditCustomer(null);
                setFormOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />새 고객 등록
            </Button>
          </div>
        </div>

        {/* KPI 카드 */}
        <CustomerSummaryCards
          stats={summaryStats}
          activeSegment={filters.segment}
          onCardClick={setSegment}
        />

        {/* 필터 바 + 뷰 토글 */}
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <CustomerFilterBar
              searchQuery={filters.searchQuery}
              onSearchChange={setSearch}
              sortOrder={filters.sortOrder}
              onSortChange={setSortOrder}
              starred={filters.starred}
              onStarredToggle={handleStarredToggle}
              sortOptions={sortOptions}
            />
          </div>
          <div className="flex items-center border rounded-md flex-shrink-0 mr-4 mt-[2px]">
            <Button
              variant={filters.viewMode === "list" ? "default" : "ghost"}
              size="sm"
              className="h-8 px-2 rounded-r-none"
              onClick={() => setViewMode("list")}
            >
              <LayoutList className="h-4 w-4" />
            </Button>
            <Button
              variant={filters.viewMode === "grid" ? "default" : "ghost"}
              size="sm"
              className="h-8 px-2 rounded-l-none"
              onClick={() => setViewMode("grid")}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* 콘텐츠 */}
      <div>
        {isLoading ? (
          <div className="divide-y">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="p-4 border-b">
                <div className="flex items-start gap-4">
                  <Skeleton className="w-11 h-11 rounded-full flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-3 w-56" />
                    <Skeleton className="h-3 w-64" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : customerData?.content && customerData.content.length > 0 ? (
          filters.viewMode === "list" ? (
            <div className="divide-y">
              {/* 전체 선택 행 */}
              <div
                className="flex items-center gap-3 px-4 py-2 bg-muted/20 border-b"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={isIndeterminate ? "indeterminate" : isAllChecked}
                  onCheckedChange={(checked) =>
                    handleCheckAll(checked === true)
                  }
                  aria-label="현재 페이지 전체 선택"
                />
                <span className="text-xs text-muted-foreground">
                  {checkedIds.size > 0
                    ? `${checkedIds.size}명 선택됨`
                    : "전체 선택"}
                </span>
              </div>
              {customerData.content.map((customer) => (
                <CustomerListCard
                  key={customer.id}
                  customer={customer}
                  onSelect={handleSelectCustomer}
                  onToggleStar={handleToggleStar}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isSelected={selectedCustomer?.id === customer.id}
                  isChecked={checkedIds.has(customer.id)}
                  onCheck={handleCheckCustomer}
                />
              ))}
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {/* 그리드 뷰 전체 선택 */}
              <div
                className="flex items-center gap-3 px-2 py-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={isIndeterminate ? "indeterminate" : isAllChecked}
                  onCheckedChange={(checked) =>
                    handleCheckAll(checked === true)
                  }
                  aria-label="현재 페이지 전체 선택"
                />
                <span className="text-xs text-muted-foreground">
                  {checkedIds.size > 0
                    ? `${checkedIds.size}명 선택됨`
                    : "전체 선택"}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {customerData.content.map((customer) => (
                  <CustomerGridCard
                    key={customer.id}
                    customer={customer}
                    onSelect={handleSelectCustomer}
                    onToggleStar={handleToggleStar}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isSelected={selectedCustomer?.id === customer.id}
                    isChecked={checkedIds.has(customer.id)}
                    onCheck={handleCheckCustomer}
                  />
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <User className="w-16 h-16 mb-4 opacity-30" />
            <p className="text-lg font-medium">등록된 고객이 없습니다</p>
            <p className="text-sm">새 고객을 등록해보세요</p>
            <Button
              className="mt-4"
              onClick={() => {
                setEditCustomer(null);
                setFormOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />새 고객 등록
            </Button>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t">
          <Select
            value={filters.pageSize.toString()}
            onValueChange={(v) => setPageSize(parseInt(v, 10))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}건씩 보기
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setPage(Math.max(0, filters.page - 1))}
              disabled={filters.page === 0}
              aria-label="이전 페이지"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground px-4">
              {filters.page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                setPage(Math.min(totalPages - 1, filters.page + 1))
              }
              disabled={filters.page >= totalPages - 1}
              aria-label="다음 페이지"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* 고객 상세 패널 */}
      <CustomerDetailSheet
        customer={selectedCustomer}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onEdit={handleEdit}
        onToggleStar={handleToggleStar}
        onCustomerUpdate={setSelectedCustomer}
      />

      {/* 고객 폼 다이얼로그 */}
      <CustomerFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditCustomer(null);
        }}
        customer={editCustomer}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={!!deleteCustomer}
        onOpenChange={() => setDeleteCustomer(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>고객 삭제</DialogTitle>
            <DialogDescription>
              정말로 &apos;{deleteCustomer?.name}&apos; 고객을 삭제하시겠습니까?
              이 작업은 되돌릴 수 없습니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCustomer(null)}>
              취소
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                deleteCustomer && deleteMutation.mutate(deleteCustomer.id)
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
